import { readJSON, removeLocalStorageKey, writeJSON } from "@/lib/local-storage"

const PENDING_INVITE_KEY = "floorpilot:pending-invite"

/** Matches the backend's INVITATION_TTL_DAYS — a stash cannot outlive the invite it points at. */
const PENDING_INVITE_TTL_MS = 7 * 24 * 60 * 60 * 1000

/** The invite a visitor is in the middle of accepting — survives every Clerk redirect. */
export type PendingInvite = {
	token: string
	/** Invited address (lowercased) — the authenticated shell only honors the stash for this user. */
	email: string
}

type StoredPendingInvite = PendingInvite & {
	/** Epoch ms when written; the stash expires on its own so a forgotten link never hijacks a later login. */
	savedAt: number
}

/**
 * Post-login intent for invites. Clerk's prebuilt widgets do full-page navigations between their
 * own steps (verify-email-address, sso-callback, …) and only carry *their* query params across
 * them, so any invite token we put in the URL is gone by the time sign-up completes. The stash
 * makes the landing spot irrelevant: `_authenticated` sends a matching signed-in user back to
 * `/invite/$token` before rendering anything.
 */
export function readPendingInvite(): PendingInvite | null {
	const value = readJSON<Partial<StoredPendingInvite>>(PENDING_INVITE_KEY)
	if (value == null || typeof value.token !== "string" || typeof value.email !== "string") return null
	// Missing or stale timestamp: drop it rather than trust an indefinitely persisted intent.
	if (typeof value.savedAt !== "number" || Date.now() - value.savedAt > PENDING_INVITE_TTL_MS) {
		clearPendingInvite()
		return null
	}
	return { token: value.token, email: value.email }
}

export function writePendingInvite(invite: PendingInvite): void {
	const stored: StoredPendingInvite = {
		token: invite.token,
		email: invite.email.trim().toLowerCase(),
		savedAt: Date.now(),
	}
	writeJSON(PENDING_INVITE_KEY, stored)
}

export function clearPendingInvite(): void {
	removeLocalStorageKey(PENDING_INVITE_KEY)
}
